/// FOURJS_START_COPYRIGHT(D,2019)
/// Property of Four Js*
/// (c) Copyright Four Js 2019, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("FilterWidget", ["WidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Filter button in ChromeBar
   * @class FilterWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   */
  cls.FilterWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.FilterWidget.prototype */ {
      __name: "FilterWidget",

      /** @type {Element} */
      _input: null,
      /** @type {Element} */
      _close: null,
      /** @type {Element} */
      _clear: null,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);

        this._input = this.getElement().getElementsByTagName("input")[0];
        this._input.setAttribute("placeholder", gbc.I18NService.t("gwc.main.chromebar.filter"));

        this._close = this.getElement().getElementsByClassName(this.getBEMPartName("close"))[0];
        this._clear = this.getElement().getElementsByClassName(this.getBEMPartName("clear"))[0];

        const onKeydown = function (evt) {
          evt.stopPropagation();
          if (evt.key.toLowerCase() === "enter") {
            context.SessionService.getCurrent()?.getCurrentApplication()?.getUI()?.getWidget()?._uiWidget?.setFocus();
          }
        };
        cls.DOMElementHelper.on(this._input, "keydown.FilterWidget", onKeydown);
        cls.DOMElementHelper.on(this._input, "input.FilterWidget", this._valueChanged.bind(this));
        cls.DOMElementHelper.domFocus(this._input);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        cls.DOMElementHelper.off(this._input, "keydown");
        cls.DOMElementHelper.off(this._input, "input");
        this._input = null;
        this._close = null;
        this._clear = null;

        $super.destroy.call(this);
      },

      /**
       * Method binded on the input key event
       */
      _valueChanged: function () {
        this.emit(context.constants.widgetEvents.valueChanged, this._input.value);
      },

      /**
       * Set filter value in the input element
       * @param {String} value - filter value
       */
      setValue: function (value) {
        this._input.value = value;
        this._valueChanged();
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this._activateButton(domEvent.target);
        return false;
      },

      /**
       * Keyboard activation of the focused clear/close button (accessibility
       * mode routes keys to the DOM-focused widget before dialog accelerators).
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this._isActivationKey(keyString, repeat) && this._activateButton(document.activeElement)) {
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Runs the action bound to the clear / close button containing `target`,
       * shared by the mouse and keyboard paths.
       * @param {?HTMLElement} target - element inside a filter button
       * @returns {boolean} true when a button action was triggered
       * @private
       */
      _activateButton: function (target) {
        if (this._clear.contains(target)) {
          this.setValue("");
          cls.DOMElementHelper.domFocus(this._input);
          return true;
        }
        if (this._close.contains(target)) {
          this.emit(context.constants.widgetEvents.close);
          return true;
        }
        return false;
      },

      /**
       * Close event is triggered when close button is pressed
       * @param {Function} hook
       */
      onClose: function (hook) {
        return this.when(context.constants.widgetEvents.close, hook);
      },

      /**
       * Value changed event is triggered when user enter key in input field
       */
      onValueChanged: function (hook) {
        return this.when(context.constants.widgetEvents.valueChanged, hook);
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Filter", cls.FilterWidget);
});
